import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, useApi, fmtDT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Card, Pill, Table, Th, Td } from "@/components/kit";
import { PhotoPicker, Photos } from "@/components/PhotoPicker";

const blank = { supplier_id: "", items: "", delivery_type: "chilled", temperature: "", packaging_ok: true, use_by_ok: true, accepted: true, rejection_reason: "", photo_ids: [] };
const LIMIT = { chilled: 8, frozen: -15 };

const Toggle = ({ value, onChange, yes, no, testid }) => (
  <div className="flex gap-2">
    {[[true, yes, "bg-emerald-600 border-emerald-600 text-white"], [false, no, "bg-rose-600 border-rose-600 text-white"]].map(([v, l, on]) => (
      <button key={l} type="button" data-testid={`${testid}-${v ? "yes" : "no"}`} onClick={() => onChange(v)} className={`ka-press flex-1 rounded-xl border py-2.5 text-sm font-semibold ${value === v ? on : "border-slate-300 bg-white"}`}>{l}</button>
    ))}
  </div>
);

export default function Deliveries() {
  const { venueId, venue, can } = useAuth();
  const { data: suppliers } = useApi("/suppliers");
  const { data, reload } = useApi(venueId ? "/deliveries" : null, { venue_id: venueId });
  const [f, setF] = useState(blank);
  const set = (k, v) => setF({ ...f, [k]: v });
  const tempBad = f.temperature !== "" && LIMIT[f.delivery_type] != null && Number(f.temperature) > LIMIT[f.delivery_type];

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/deliveries", { ...f, venue_id: venueId, temperature: f.temperature === "" ? null : Number(f.temperature) });
      toast.success(f.accepted ? "Delivery accepted" : "Delivery rejected — incident raised");
      setF(blank);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <PageHeader eyebrow={venue?.name} title="Deliveries" subtitle="Goods-in checks against your approved suppliers. Rejections raise an incident automatically." />
      {can("record") && (
        <Card className="mb-8">
          {suppliers && !suppliers.length ? (
            <p className="text-sm text-slate-600">Add a supplier first in <Link to="/app/suppliers" className="font-semibold text-emerald-700 underline">Suppliers</Link>.</p>
          ) : (
            <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
              <Field label="Supplier *"><select data-testid="delivery-supplier-select" required className="ka-input" value={f.supplier_id} onChange={(e) => set("supplier_id", e.target.value)}><option value="">Choose…</option>{(suppliers || []).map((s) => <option key={s.id} value={s.id}>{s.name}{s.approved ? "" : " (not approved)"}</option>)}</select></Field>
              <Field label="Delivery type"><select data-testid="delivery-type-select" className="ka-input" value={f.delivery_type} onChange={(e) => set("delivery_type", e.target.value)}><option value="chilled">Chilled (≤ 8°C)</option><option value="frozen">Frozen (≤ -15°C)</option><option value="ambient">Ambient</option></select></Field>
              <Field label="Probe temperature °C">
                <input data-testid="delivery-temp-input" type="number" step="0.1" className={`ka-input ${tempBad ? "border-rose-500 text-rose-700" : ""}`} value={f.temperature} onChange={(e) => set("temperature", e.target.value)} />
                {tempBad && <span data-testid="delivery-temp-warning" className="mt-1 block text-xs font-semibold text-rose-600">Out of range — this delivery must be rejected.</span>}
              </Field>
              <Field label="Items received *" className="sm:col-span-2"><input data-testid="delivery-items-input" required className="ka-input" value={f.items} onChange={(e) => set("items", e.target.value)} placeholder="e.g. 10kg chicken thighs, 6L milk" /></Field>
              <Field label="Packaging intact?"><Toggle testid="delivery-packaging" value={f.packaging_ok} onChange={(v) => set("packaging_ok", v)} yes="Yes" no="No" /></Field>
              <Field label="Use-by dates OK?"><Toggle testid="delivery-useby" value={f.use_by_ok} onChange={(v) => set("use_by_ok", v)} yes="Yes" no="No" /></Field>
              <Field label="Decision"><Toggle testid="delivery-accepted" value={f.accepted} onChange={(v) => set("accepted", v)} yes="Accept" no="Reject" /></Field>
              {!f.accepted && <Field label="Reason for rejection *"><input data-testid="delivery-reason-input" required className="ka-input" value={f.rejection_reason} onChange={(e) => set("rejection_reason", e.target.value)} /></Field>}
              <Field label={f.accepted ? "Photos (optional)" : "Photo proof of the problem"} hint={f.accepted ? "e.g. delivery note or probe reading" : "e.g. damaged packaging, the probe reading, out-of-date labels"} className="sm:col-span-2">
                <PhotoPicker testid="delivery-photo" value={f.photo_ids} onChange={(v) => set("photo_ids", v)} />
              </Field>
              <div className="sm:col-span-2"><button data-testid="submit-delivery-button" className="ka-press rounded-full bg-emerald-600 px-8 py-3 text-sm font-semibold text-white hover:bg-emerald-700">Record delivery</button></div>
            </form>
          )}
        </Card>
      )}
      <Table testid="deliveries-table">
        <thead><tr><Th>Received</Th><Th>Supplier</Th><Th>Items</Th><Th>Temp</Th><Th>Checks</Th><Th>Decision</Th></tr></thead>
        <tbody>
          {(data || []).map((d) => (
            <tr key={d.id} data-testid={`delivery-row-${d.id}`}>
              <Td className="font-mono text-xs">{fmtDT(d.received_at)}<div className="font-sans text-slate-500">{d.received_by}</div></Td>
              <Td className="font-medium text-slate-900">{d.supplier_name}</Td>
              <Td>{d.items}</Td>
              <Td>{d.temperature != null ? <span className={d.temp_out_of_range ? "font-semibold text-rose-600" : ""}>{d.temperature}°C</span> : "—"}</Td>
              <Td className="text-xs">Packaging {d.packaging_ok ? "✓" : "✗"} · Dates {d.use_by_ok ? "✓" : "✗"}</Td>
              <Td><Pill status={d.accepted ? "ok" : "overdue"} label={d.accepted ? "Accepted" : "Rejected"} />{d.rejection_reason && <div className="mt-1 text-xs text-slate-500">{d.rejection_reason}</div>}<div className="mt-1"><Photos ids={d.photo_ids} size="h-10 w-10" /></div></Td>
            </tr>
          ))}
          {data && !data.length && <tr><Td className="text-slate-500">No deliveries recorded.</Td></tr>}
        </tbody>
      </Table>
    </div>
  );
}
